<script setup>
import { Head, Link } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { useCopy } from '@/composables/useCopy';

const props = defineProps({
    domain: Object, service: String, branding: Object, term: Object,
    relatedTerms: { type: Array, default: () => [] }, seoTitle: { type: String, default: '' },
    metaDescription: { type: String, default: '' }, jsonLd: Object, designVariant: Object,
    funnelVariant: { type: Object, default: () => ({}) },
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) },
});

const { t, ts, spot } = useCopy(props);

const categoryLabels = { grundlagen: 'Grundlagen', technik: 'Technik', wirtschaftlichkeit: 'Wirtschaftlichkeit', 'förderung': 'Förderung', installation: 'Installation', recht: 'Recht' };
</script>

<template>
    <Head :title="seoTitle"><meta :content="metaDescription" name="description" /><script v-if="jsonLd" type="application/ld+json" v-text="JSON.stringify(jsonLd)" /></Head>
    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="glossary" heroMode>
        <section class="sv-gterm-hero">
            <div class="sv-gterm-hero__bg" aria-hidden="true"></div>
            <div class="sv-gterm-hero__content">
                <nav class="sv-gterm-hero__bread"><Link href="/">{{ t('glossaryterm_bread_home', 'Startseite') }}</Link> / <Link href="/glossar">{{ t('glossaryterm_bread_overview', 'Glossar') }}</Link> / <span>{{ term.term }}</span></nav>
                <span v-if="term.category" class="sv-gterm-hero__cat">{{ categoryLabels[term.category] || term.category }}</span>
                <h1 class="sv-gterm-hero__title">{{ term.term }}</h1>
                <p class="sv-gterm-hero__def">{{ term.short_definition }}</p>
            </div>
        </section>

        <article class="sv-gterm-body">
            <div class="sv-gterm-body__inner">
                <div class="sv-gterm-body__card">
                    <div class="sv-gterm-body__prose" v-html="term.body" />
                </div>

                <div v-if="relatedTerms.length" class="sv-gterm-related">
                    <h2 class="sv-gterm-related__heading">{{ t('glossaryterm_related_heading', 'Verwandte Begriffe') }}</h2>
                    <div class="sv-gterm-related__grid">
                        <Link v-for="r in relatedTerms" :key="r.slug" :href="`/glossar/${r.slug}`" class="sv-gterm-related__card">
                            <h3 class="sv-gterm-related__card-title">{{ r.term }}</h3>
                            <p class="sv-gterm-related__card-def">{{ r.short_definition }}</p>
                        </Link>
                    </div>
                </div>
            </div>
        </article>

</AppLayout>
</template>

<style scoped>
.sv-gterm-hero { position: relative; min-height: 320px; display: flex; align-items: center; background: #0a1628; overflow: hidden; }
.sv-gterm-hero__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #0a1628, #001d75); }
.sv-gterm-hero__content { position: relative; z-index: 2; max-width: 720px; margin: 0 auto; padding: 100px 24px 48px; text-align: center; }
.sv-gterm-hero__bread { font-family: 'Inter', sans-serif; font-size: 12px; color: rgba(255,255,255,0.4); margin-bottom: 16px; }
.sv-gterm-hero__bread a { color: rgba(255,255,255,0.5); text-decoration: none; }
.sv-gterm-hero__bread a:hover { color: #fff; }
.sv-gterm-hero__cat { display: inline-block; padding: 4px 14px; border-radius: 16px; font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 600; background: rgba(0,215,130,0.1); color: #00d782; margin-bottom: 12px; }
.sv-gterm-hero__title { font-family: 'Inter', sans-serif; font-size: clamp(28px, 4vw, 44px); font-weight: 800; color: #fff; margin-bottom: 12px; }
.sv-gterm-hero__def { font-family: 'Inter', sans-serif; font-size: 17px; color: rgba(255,255,255,0.65); line-height: 1.6; }

.sv-gterm-body { padding: 0 0 80px; background: #fff; }
.sv-gterm-body__inner { max-width: 720px; margin: 0 auto; padding: 0 24px; }
.sv-gterm-body__card { padding: 36px; background: #fff; border-radius: 16px; border: 1px solid #e2e8f0; margin-top: -20px; position: relative; z-index: 4; }
.sv-gterm-body__prose { font-family: 'Inter', sans-serif; font-size: 16px; color: #1a1a1a; line-height: 1.8; }
.sv-gterm-body__prose :deep(h2) { font-size: 22px; font-weight: 700; margin-top: 1.8em; margin-bottom: 0.5em; }
.sv-gterm-body__prose :deep(h3) { font-size: 18px; font-weight: 600; margin-top: 1.5em; }
.sv-gterm-body__prose :deep(p) { margin-bottom: 1em; }
.sv-gterm-body__prose :deep(a) { color: #001d75; }
.sv-gterm-body__prose :deep(ul), .sv-gterm-body__prose :deep(ol) { padding-left: 1.5em; margin-bottom: 1em; }

.sv-gterm-related { margin-top: 48px; }
.sv-gterm-related__heading { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 700; color: #1a1a1a; margin-bottom: 16px; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; }
.sv-gterm-related__grid { display: grid; gap: 12px; grid-template-columns: 1fr; }
@media (min-width: 640px) { .sv-gterm-related__grid { grid-template-columns: repeat(2, 1fr); } }
.sv-gterm-related__card { padding: 16px; background: #0f1d32; border-radius: 12px; text-decoration: none; transition: transform 200ms; }
.sv-gterm-related__card:hover { transform: translateY(-2px); }
.sv-gterm-related__card-title { font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700; color: #fff; margin-bottom: 4px; }
.sv-gterm-related__card-def { font-family: 'Inter', sans-serif; font-size: 12px; color: rgba(255,255,255,0.5); line-height: 1.5; }
</style>
